import { PositionType } from '../../lib/defs/types';
import { BlockComponent } from '../components/BlockComponent';
import {
  CellLayer,
  CellLayerId,
  MapComponent,
} from '../components/MapComponent';
import { BlockId, blockPropsMap } from '../defs/block';
import { CollideId, Collider } from '../defs/collide';

/**
 * Create a new collision tracker.
 */
export function getCollisionTracker() {
  return new CollisionTracker();
}

/*
    tracks unique collisions with target blocks
*/
export class CollisionTracker {
  // fields
  //-------------------------------------------------------------------------
  private _collided: BlockComponent[] = [];
  private _collidedAt: { cell: string[]; base: string[]; overlay: string[] } = {
    cell: [],
    base: [],
    overlay: [],
  };

  // api
  //-------------------------------------------------------------------------
  public reset() {
    // clear collided list
    this._collided = [];
    this._collidedAt.cell.length = 0;
    this._collidedAt.base.length = 0;
    this._collidedAt.overlay.length = 0;
  }

  public collide(
    block: BlockComponent,
    collisionId: CollideId,
    collider?: Collider,
  ): boolean {
    // if not multi-collidable and already collided, skip
    if (
      !this._isMultiCollidable(block.blockId) &&
      this._collided.includes(block)
    )
      return false;

    // add to collided list
    this._collided.push(block);

    // collide
    block.collide(collisionId, collider);

    return true;
  }

  public collideAt(
    map: MapComponent,
    position: PositionType,
    collisionId: CollideId,
    collider?: Collider,
  ): boolean {
    // get cell at this position
    const cell = map.getCellAt(position);
    if (!cell) return false;

    const cellAt = `${position.x},${position.y}`;

    // If collider type is not 'block', skip if already collided at this cell for this cycle
    /**
     * Since only for 'block' based collisions, multiple block destroy can happen on the same cell
     * at the same time within the same tracker round, eg. a complete basic group surrounding a 1x1 obstacle
     * should bed collided 4 times, while on a similar scenario surrounding a 2x2 obstacle, each occupied
     * obstacle cell should be collided twice per cell, resulting in a 8 times in total. Therefore, we should
     * skip this once-per-cell condition for 'block' collider type.
     */

    if (collider?.type !== 'block' && this._collidedAt.cell.includes(cellAt))
      return false;
    this._collidedAt.cell.push(cellAt);

    const { base, overlay } = cell;
    const baseCollided = base
      ? this._collideAtCellLayer(base, 'base', collisionId, collider)
      : false;
    const overlayCollided = overlay
      ? this._collideAtCellLayer(overlay, 'overlay', collisionId, collider)
      : false;

    return baseCollided || overlayCollided;
  }

  private _collideAtCellLayer(
    layer: CellLayer,
    layerId: CellLayerId,
    collisionId: CollideId,
    collider?: Collider,
  ) {
    const entityPosition = layer.entity.c.position.mapPosition;
    const width = layer.entity.c.block.width;
    const height = layer.entity.c.block.height;
    const ats: string[] = [];

    /**
     * Collide at all cells occupied by the block on the layer as a whole,
     * treating it as a single hit per block for cases where its size is larger than 1x1.
     */
    for (let x = 0; x < width; x++) {
      for (let y = 0; y < height; y++) {
        const at = `${entityPosition.x + x},${entityPosition.y + y}`;

        const isMultiCollidable = this._isMultiCollidable(
          layer.entity.c.block.blockId,
        );
        const blockAlreadyCollided = this._collidedAt[layerId].includes(at);

        // skip if not multi-collidable and already collided at this position for this cycle
        if (!isMultiCollidable && blockAlreadyCollided) return false;

        // add to 'collided at' pre-list
        if (!blockAlreadyCollided) ats.push(at);
      }
    }

    // collide layer and add to the 'collided at' list of the corresponded layer, if successful
    const success = this.collide(layer.entity.c.block, collisionId, collider);
    if (success) this._collidedAt[layerId].push(...ats);

    return success;
  }

  private _isMultiCollidable(blockId: BlockId) {
    return !!blockPropsMap[blockId].multiCollidable;
  }
}
